/**
 * Typed API client — thin fetch wrappers for every endpoint.
 * All methods throw on non-2xx so react-query can handle errors.
 */

const BASE = '/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    credentials: 'include',
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any)
    const err = new Error((body as any)?.error?.message || `API ${res.status}`)
    ;(err as any).status = res.status
    ;(err as any).body = body
    throw err
  }
  return res.json()
}

// ─── Clients ─────────────────────────────────────────────────────

export interface Client {
  id: string
  name: string
  vertical: string | null
  plan: string | null
  status: string
  health: string
  mrr: number | null
  website: string | null
  notes: string | null
  kind: 'client' | 'internal'
  createdAt: string
  updatedAt: string
}

export function listClients(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<{ items: Client[] }>(`/clients${qs}`)
}

export function getClient(id: string) {
  return request<Client & { contacts: any[]; assignments: any[] }>(`/clients/${id}`)
}

export function createClient(data: Partial<Client>) {
  return request<Client>('/clients', { method: 'POST', body: JSON.stringify(data) })
}

export function updateClient(id: string, data: Partial<Client>) {
  return request<Client>(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteClient(id: string) {
  return request<{ ok: boolean }>(`/clients/${id}`, { method: 'DELETE' })
}

export function importClients(clients: Partial<Client>[]) {
  return request<{ items: Client[]; count: number }>('/clients/import', {
    method: 'POST',
    body: JSON.stringify({ clients }),
  })
}

export interface Contact {
  id: string
  clientId: string
  name: string
  email: string | null
  phone: string | null
  role: string | null
  primary: string
  createdAt: string
}

export function createContact(
  clientId: string,
  data: { name: string; email?: string; phone?: string; role?: string; primary?: 'true' | 'false' },
) {
  return request<Contact>(`/clients/${clientId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ─── Tasks ───────────────────────────────────────────────────────

export interface Task {
  id: string
  title: string
  description: string | null
  discipline: string
  status: string
  priority: string
  clientId: string | null
  /**
   * Derived on read by the API (never a column). Present on every task read so
   * a card can say whose work it is without a second request — and so it still
   * renders for a member, who cannot list the clients they aren't assigned to.
   */
  clientName: string | null
  projectId: string | null
  start: string | null
  due: string | null
  estimateHrs: number | null
  completedAt: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
}

export function listTasks(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<{ items: Task[] }>(`/tasks${qs}`)
}

export function getTask(id: string) {
  return request<Task & { assignments: any[] }>(`/tasks/${id}`)
}

export function createTask(data: { title: string; [key: string]: any }) {
  return request<Task>('/tasks', { method: 'POST', body: JSON.stringify(data) })
}

export function updateTask(id: string, data: Partial<Task>) {
  return request<Task>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteTask(id: string) {
  return request<{ ok: boolean }>(`/tasks/${id}`, { method: 'DELETE' })
}

export function archiveTask(id: string) {
  return request<Task>(`/tasks/${id}/archive`, { method: 'POST', body: '{}' })
}

export function unarchiveTask(id: string) {
  return request<Task>(`/tasks/${id}/unarchive`, { method: 'POST', body: '{}' })
}

/**
 * `assigneeIds` is not a task field — the import endpoint turns it into
 * `assignments` rows. It rides on the request so a sheet with an Assignee
 * column doesn't need one extra round trip per row.
 */
export type ImportTaskDraft = Partial<Task> & { assigneeIds?: string[] }

export function importTasks(tasks: ImportTaskDraft[]) {
  return request<{ items: Task[]; count: number }>('/tasks/import', {
    method: 'POST',
    body: JSON.stringify({ tasks }),
  })
}

/**
 * Bulk update due dates on selected tasks.
 * @param taskIds - Array of task IDs to update
 * @param due - Specific due date (YYYY-MM-DD), or undefined for shift mode
 * @param dayShift - Number of days to shift, or undefined for set mode
 */
export function bulkUpdateTaskDue(taskIds: string[], due?: string, dayShift?: number) {
  return request<{ items: Task[]; count: number }>('/tasks/bulk-due', {
    method: 'POST',
    body: JSON.stringify({ taskIds, ...(due !== undefined && { due }), ...(dayShift !== undefined && { dayShift }) }),
  })
}

// ─── Task Links ──────────────────────────────────────────────────

export interface TaskLink {
  id: string
  taskId: string
  url: string
  label: string | null
  createdBy: string | null
  createdAt: string
}

export function listTaskLinks(taskId: string) {
  return request<{ items: TaskLink[] }>(`/tasks/${taskId}/links`)
}

export function createTaskLink(taskId: string, data: { url: string; label?: string }) {
  return request<TaskLink>(`/tasks/${taskId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function deleteTaskLink(taskId: string, linkId: string) {
  return request<{ ok: boolean }>(`/tasks/${taskId}/links/${linkId}`, { method: 'DELETE' })
}

// ─── Task Attachments ────────────────────────────────────────────

export interface TaskAttachment {
  id: string
  taskId: string
  filename: string
  mimeType: string | null
  sizeBytes: number | null
  storageKey: string
  uploadedBy: string | null
  createdAt: string
}

export function listTaskAttachments(taskId: string) {
  return request<{ items: TaskAttachment[] }>(`/tasks/${taskId}/attachments`)
}

/**
 * `filename` overrides the name the file carries. Clipboard images arrive
 * unnamed, and the multipart filename is the only way to label them without
 * reconstructing the File — which the Workers types in this project make
 * awkward, and which would copy the bytes for nothing.
 */
export async function uploadTaskAttachment(
  taskId: string,
  file: File,
  filename?: string,
): Promise<TaskAttachment> {
  const formData = new FormData()
  if (filename) formData.append('file', file, filename)
  else formData.append('file', file)

  const res = await fetch(`${BASE}/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    // No Content-Type header — browser sets multipart boundary automatically
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any)
    const err = new Error((body as any)?.error?.message || `API ${res.status}`)
    ;(err as any).status = res.status
    throw err
  }
  return res.json()
}

export function downloadTaskAttachmentUrl(taskId: string, attachmentId: string): string {
  return `${BASE}/tasks/${taskId}/attachments/${attachmentId}/download`
}

export function deleteTaskAttachment(taskId: string, attachmentId: string) {
  return request<{ ok: boolean }>(`/tasks/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' })
}

// ─── Task Comments ───────────────────────────────────────────────

export interface TaskComment {
  id: string
  taskId: string
  body: string
  authorId: string | null
  createdAt: string
  updatedAt: string
}

export function listTaskComments(taskId: string) {
  return request<{ items: TaskComment[] }>(`/tasks/${taskId}/comments`)
}

export function createTaskComment(taskId: string, data: { body: string }) {
  return request<TaskComment>(`/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateTaskComment(taskId: string, commentId: string, data: { body: string }) {
  return request<TaskComment>(`/tasks/${taskId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function deleteTaskComment(taskId: string, commentId: string) {
  return request<{ ok: boolean }>(`/tasks/${taskId}/comments/${commentId}`, { method: 'DELETE' })
}

// ─── Client Notes ────────────────────────────────────────────────

export const CLIENT_NOTE_CATEGORIES = [
  'brand_voice',
  'goals',
  'audience',
  'process',
  'lifecycle',
  'general',
] as const
export type ClientNoteCategory = (typeof CLIENT_NOTE_CATEGORIES)[number]

export interface ClientNote {
  id: string
  clientId: string
  title: string
  category: string
  body: string
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export function listClientNotes(clientId: string) {
  return request<{ items: ClientNote[] }>(`/clients/${clientId}/notes`)
}

export function createClientNote(
  clientId: string,
  data: { title: string; category?: ClientNoteCategory; body: string },
) {
  return request<ClientNote>(`/clients/${clientId}/notes`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateClientNote(
  clientId: string,
  noteId: string,
  data: { title?: string; category?: ClientNoteCategory; body?: string },
) {
  return request<ClientNote>(`/clients/${clientId}/notes/${noteId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function deleteClientNote(clientId: string, noteId: string) {
  return request<{ ok: boolean }>(`/clients/${clientId}/notes/${noteId}`, { method: 'DELETE' })
}

// ─── Client Links ────────────────────────────────────────────────

export interface ClientLink {
  id: string
  clientId: string
  url: string
  label: string | null
  createdBy: string | null
  createdAt: string
}

export function listClientLinks(clientId: string) {
  return request<{ items: ClientLink[] }>(`/clients/${clientId}/links`)
}

export function createClientLink(clientId: string, data: { url: string; label?: string }) {
  return request<ClientLink>(`/clients/${clientId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function deleteClientLink(clientId: string, linkId: string) {
  return request<{ ok: boolean }>(`/clients/${clientId}/links/${linkId}`, { method: 'DELETE' })
}

// ─── Client Attachments ──────────────────────────────────────────

export interface ClientAttachment {
  id: string
  clientId: string
  filename: string
  mimeType: string | null
  sizeBytes: number | null
  storageKey: string
  uploadedBy: string | null
  createdAt: string
}

export function listClientAttachments(clientId: string) {
  return request<{ items: ClientAttachment[] }>(`/clients/${clientId}/attachments`)
}

export async function uploadClientAttachment(clientId: string, file: File): Promise<ClientAttachment> {
  const formData = new FormData()
  formData.append('file', file)

  const res = await fetch(`${BASE}/clients/${clientId}/attachments`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    // No Content-Type header — browser sets multipart boundary automatically
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any)
    const err = new Error((body as any)?.error?.message || `API ${res.status}`)
    ;(err as any).status = res.status
    throw err
  }
  return res.json()
}

export function downloadClientAttachmentUrl(clientId: string, attachmentId: string): string {
  return `${BASE}/clients/${clientId}/attachments/${attachmentId}/download`
}

export function deleteClientAttachment(clientId: string, attachmentId: string) {
  return request<{ ok: boolean }>(`/clients/${clientId}/attachments/${attachmentId}`, { method: 'DELETE' })
}

// ─── Employees ───────────────────────────────────────────────────

/**
 * The roster as everyone sees it: enough to render an assignee chip, and no
 * more. The server redacts contact details, platform role and login state for
 * anyone below admin (see `toDirectory` in src/api/employees.ts), so those
 * fields live on `ManagedEmployee` and are only ever populated for a manager.
 */
export interface Employee {
  id: string
  name: string
  initials: string
  title: string | null
  disciplines: string[]
  avatarColor: string
  active: boolean
  createdAt: string
  updatedAt: string
  /**
   * Admins only. Optional here so UI that merely wants a mailto shortcut
   * (assignee chips) still type-checks against a directory row — it must
   * degrade when the field is absent rather than assume it.
   */
  email?: string
}

/** The admin view of a roster row — only returned to owner/admin/super_admin. */
export interface ManagedEmployee extends Employee {
  email: string
  managerId: string | null
  capacityHrsWeek: number | null
  role: string
  inviteStatus: string
  /** Derived server-side from the auth tables — don't infer from inviteStatus. */
  hasLogin: boolean
  hasPassword: boolean
  userId: string | null
}

export function listEmployees() {
  return request<{ items: Employee[] }>('/employees')
}

/**
 * The same endpoint, typed as the admin shape. Only call it behind a
 * `roleCanManage` check — for anyone else the server returns the redacted
 * directory rows and the extra fields will be missing.
 */
export function listTeam() {
  return request<{ items: ManagedEmployee[] }>('/employees')
}

export interface EmployeeInput {
  name: string
  initials: string
  email: string
  avatarColor: string
  title?: string
  role?: string
  disciplines?: string[]
  capacityHrsWeek?: number
  inviteStatus?: string
  active?: boolean
}

export function createEmployee(data: EmployeeInput) {
  return request<ManagedEmployee & { inviteError?: string }>('/employees', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updateEmployee(id: string, data: Partial<EmployeeInput>) {
  return request<ManagedEmployee>(`/employees/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteEmployee(id: string) {
  return request<{ ok: boolean }>(`/employees/${id}`, { method: 'DELETE' })
}

/** Creates the login if needed and emails a set-password link (also the resend path). */
export function inviteEmployee(id: string) {
  return request<ManagedEmployee>(`/employees/${id}/invite`, { method: 'POST' })
}

/** Deletes the login and drops the member back to roster-only. */
export function revokeEmployeeAccess(id: string) {
  return request<ManagedEmployee>(`/employees/${id}/revoke`, { method: 'POST' })
}

export function importEmployees(
  employees: {
    name: string
    initials: string
    email: string
    avatarColor: string
    title?: string
    role?: string
    disciplines?: string[]
    capacityHrsWeek?: number
    inviteStatus?: string
  }[],
) {
  return request<{ items: ManagedEmployee[]; count: number; skipped: { email: string; reason: string }[] }>(
    '/employees/import',
    { method: 'POST', body: JSON.stringify({ employees }) },
  )
}

// ─── Assignments ─────────────────────────────────────────────────

export interface Assignment {
  id: string
  employeeId: string
  entityType: string
  entityId: string
  role: string
  allocationPct: number | null
  assignedBy: string | null
  createdAt: string
}

export function listAssignments(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<{ items: Assignment[] }>(`/assignments${qs}`)
}

export function createAssignment(data: {
  employeeId: string
  entityType: string
  entityId: string
  role: string
}) {
  return request<Assignment>('/assignments', { method: 'POST', body: JSON.stringify(data) })
}

export function updateAssignment(id: string, patch: { role?: string; allocationPct?: number | null }) {
  return request<Assignment>(`/assignments/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

export function deleteAssignment(id: string) {
  return request<{ ok: boolean }>(`/assignments/${id}`, { method: 'DELETE' })
}

// ─── Contracts (SOW) ─────────────────────────────────────────────

export interface Contract {
  id: string
  clientId: string
  name: string
  status: string
  term: string
  tier: string
  modules: string[]
  mrr: number | null
  contractValue: number | null
  startDate: string | null
  endDate: string | null
  renewalDate: string | null
  signedBy: string | null
  docUrl: string | null
  createdAt: string
  updatedAt: string
}

export function listContracts(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<{ items: Contract[] }>(`/contracts${qs}`)
}

export function getContract(id: string) {
  return request<Contract & { projects: Project[] }>(`/contracts/${id}`)
}

export function createContract(data: Partial<Contract>) {
  return request<Contract>('/contracts', { method: 'POST', body: JSON.stringify(data) })
}

export function updateContract(id: string, data: Partial<Contract>) {
  return request<Contract>(`/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteContract(id: string) {
  return request<{ ok: boolean }>(`/contracts/${id}`, { method: 'DELETE' })
}

// ─── Projects ────────────────────────────────────────────────────

export interface Project {
  id: string
  name: string
  clientId: string
  contractId: string | null
  tier: string
  modules: string[]
  status: string
  start: string | null
  due: string | null
  health: string | null
  totalTasks?: number
  doneTasks?: number
  progress?: number
  createdAt: string
  updatedAt: string
}

export function listProjects(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return request<{ items: Project[] }>(`/projects${qs}`)
}

export function getProject(id: string) {
  return request<Project & { tasks: Task[]; assignments: Assignment[]; totalTasks: number; doneTasks: number; progress: number }>(`/projects/${id}`)
}

export function createProject(data: Partial<Project>) {
  return request<Project>('/projects', { method: 'POST', body: JSON.stringify(data) })
}

export function updateProject(id: string, data: Partial<Project>) {
  return request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteProject(id: string) {
  return request<{ ok: boolean }>(`/projects/${id}`, { method: 'DELETE' })
}

// ─── Events (activity timeline) ──────────────────────────────────

export interface Event {
  id: string
  entityType: string
  entityId: string
  type: string
  fromState: string | null
  toState: string | null
  actor: string
  meta: Record<string, unknown>
  at: string
}

export function listEvents(params: { entityType: string; entityId: string; limit?: number }) {
  const qs = new URLSearchParams({
    entityType: params.entityType,
    entityId: params.entityId,
    ...(params.limit ? { limit: String(params.limit) } : {}),
  }).toString()
  return request<{ items: Event[] }>(`/events?${qs}`)
}

// ─── Auth ────────────────────────────────────────────────────────

export function getSession() {
  return request<{ user: any; session: any } | null>('/auth/get-session')
}

export function signIn(email: string, password: string) {
  return request<{ user: any; session: any }>('/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })
}

// No signUp() wrapper on purpose: public registration is disabled
// (`disableSignUp` in src/auth/index.ts) and logins are minted only by
// owner/admin via inviteEmployee(). A self-registered account would have no
// employee link and the API would refuse it anyway.

export function signOut() {
  // Better Auth 500s on an empty body when Content-Type is json — always send {}.
  return request<{ success: boolean }>('/auth/sign-out', { method: 'POST', body: '{}' })
}

export function requestPasswordReset(email: string) {
  return request<{ status: boolean }>('/auth/request-password-reset', {
    method: 'POST',
    body: JSON.stringify({ email, redirectTo: `${window.location.origin}/reset-password` }),
  })
}

export function resetPassword(token: string, newPassword: string) {
  return request<{ status: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  })
}

// ─── Settings ────────────────────────────────────────────────────

export interface Settings {
  disciplines: string[]
  updatedAt: string
}

export function getSettings() {
  return request<Settings>('/settings')
}

export function updateDisciplines(disciplines: string[]) {
  return request<Settings>('/settings/disciplines', {
    method: 'PATCH',
    body: JSON.stringify({ disciplines }),
  })
}

// ─── Assistant (chat-agent Worker) ───────────────────────────────
// Proxied through /api/agent/* — see src/api/agent.ts. Types mirror
// workers/chat-agent/src/agents/request-intake.ts (IntakeView).

export type IntakeStatus = 'gathering' | 'ready' | 'submitted' | 'needs_human' | 'discarded'
export type RequestKind = 'feature' | 'bug' | 'question' | 'chore'

export interface RequestDraft {
  kind: RequestKind | null
  title: string | null
  summary: string | null
  area: string | null
  priority: string | null
  stepsToReproduce: string | null
  expectedBehavior: string | null
  actualBehavior: string | null
  impact: string | null
  sourceUrl: string | null
}

export interface IntakeMessage {
  id: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  toolName: string | null
  at: string
}

export interface IntakeView {
  requestId: string
  status: IntakeStatus
  draft: RequestDraft
  issue: { key: string; url: string } | null
  missing: string[]
  submittable: boolean
  escalationReason: string | null
  createdAt: string
  updatedAt: string
  reply?: string
  messages?: IntakeMessage[]
}

export function startIntake(input: { message?: string; sourceUrl?: string } = {}) {
  return request<IntakeView>('/agent/intake', {
    method: 'POST',
    body: JSON.stringify({
      ...(input.message ? { message: input.message } : {}),
      sourceUrl: input.sourceUrl ?? window.location.href,
    }),
  })
}

export function getIntake(requestId: string) {
  return request<IntakeView>(`/agent/intake/${requestId}`)
}

export function sendIntakeMessage(requestId: string, message: string) {
  return request<IntakeView>(`/agent/intake/${requestId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  })
}

/** Explicit "file it" — independent of whether the model called its own tool. */
export function submitIntake(requestId: string) {
  return request<IntakeView>(`/agent/intake/${requestId}/submit`, { method: 'POST', body: '{}' })
}

export function discardIntake(requestId: string) {
  return request<IntakeView>(`/agent/intake/${requestId}/discard`, { method: 'POST', body: '{}' })
}

export interface AgentHealth {
  ok: boolean
  service: string
  env: string
  model: string
  integrations: {
    tracker: { name: string; live: boolean }
    notifier: { name: string; live: boolean }
  }
}

export function getAgentHealth() {
  return request<AgentHealth>('/agent/health')
}
